import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CookieIcon } from "@phosphor-icons/react";

const KEY = "gdl_cookie_consent";

export function recordConsent(choice) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ choice, ts: new Date().toISOString() }));
  } catch (_) {}
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch (_) { setOpen(true); }
    const reopen = () => setOpen(true);
    window.addEventListener("open-cookie-preferences", reopen);
    return () => window.removeEventListener("open-cookie-preferences", reopen);
  }, []);

  // While the banner is visible, reserve space at the bottom of the page so it
  // never overlaps the last block of content (e.g. the final decision-lens answer).
  useEffect(() => {
    if (!open) { document.body.style.paddingBottom = ""; return; }
    const apply = () => {
      const h = barRef.current?.offsetHeight || 0;
      document.body.style.paddingBottom = h ? `${h + 16}px` : "";
    };
    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    if (ro && barRef.current) ro.observe(barRef.current);
    window.addEventListener("resize", apply);
    return () => {
      document.body.style.paddingBottom = "";
      if (ro) ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [open]);

  const decide = (choice) => { recordConsent(choice); setOpen(false); };

  if (!open) return null;

  return (
    <div ref={barRef} data-testid="cookie-consent" className="fixed bottom-0 inset-x-0 z-[60] p-3 sm:p-4">
      <div className="max-w-screen-lg mx-auto rounded-xl border border-zinc-700 bg-[#0A0F1E]/95 backdrop-blur-md shadow-2xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <CookieIcon size={22} weight="duotone" className="text-signal shrink-0 mt-0.5" />
          <p className="text-[13px] leading-relaxed text-zinc-300">
            We use only <strong className="text-zinc-100">strictly necessary cookies</strong> to sign you in and keep the site secure — no advertising or tracking. See our{" "}
            <Link to="/privacy" className="text-signal underline decoration-dotted underline-offset-2">Privacy Policy</Link>. You can manage cookies in your browser anytime.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="cookie-reject-btn"
            onClick={() => decide("essential_only")}
            className="px-4 py-2 rounded-md text-[13px] font-medium border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            Essential only
          </button>
          <button
            data-testid="cookie-accept-btn"
            onClick={() => decide("accepted")}
            className="px-4 py-2 rounded-md text-[13px] font-semibold bg-signal hover:bg-signal-dark text-zinc-950 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
